import { Fragment, useEffect, useState } from "react";
import {
    Button,
    Card,
    Col,
    Form,
    Row,
    Spinner,
    Table,
} from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Select from "react-select";
import swal from "sweetalert";
import api, { getFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        ["clean"],
    ],
};

const customSelectStyles = {
    control: (base) => ({
        ...base,
        height: "3.5rem",
        minHeight: "3.5rem",
        display: "flex",
        alignItems: "center",
        borderRadius: "1rem",
        borderColor: "#d7dae3",
        boxShadow: "none",
        "&:hover": {
            borderColor: "#d7dae3",
        },
    }),
    valueContainer: (base) => ({
        ...base,
        height: "100%",
        padding: "0 1.25rem",
        display: "flex",
        alignItems: "center",
    }),
    input: (base) => ({
        ...base,
        margin: "0",
        padding: "0",
    }),
    singleValue: (base) => ({
        ...base,
        margin: 0,
        lineHeight: "1",
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: "100%",
    }),
    dropdownIndicator: (base) => ({
        ...base,
        paddingTop: "0.25rem",
        paddingBottom: "0.25rem",
    }),
    clearIndicator: (base) => ({
        ...base,
        paddingTop: "0.25rem",
        paddingBottom: "0.25rem",
    }),
    placeholder: (base) => ({
        ...base,
        color: "#6e6e6e",
    }),
};

const compactControlHeight = 38;

const DprEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [photos, setPhotos] = useState([]);
    const [searchParams] = useSearchParams();
    const workOrderFromUrl = searchParams.get("workOrder") || "";
    const [workOrders, setWorkOrders] = useState([]);
    const [boqItems, setBoqItems] = useState([]);
    const [activityMasters, setActivityMasters] = useState([]);
    const [units, setUnits] = useState([]);
    const [dprUsageMap, setDprUsageMap] = useState({});
    const [tasks, setTasks] = useState([]);
    const [selectedTask, setSelectedTask] = useState(null);
    const [previousOpenIssues, setPreviousOpenIssues] = useState([]);
    const [previousIssueUpdates, setPreviousIssueUpdates] = useState({});

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [manualItem, setManualItem] = useState({
        activity: "",
        quantity: "",
        unit: "",
        location: "",
        wastage: 0,
        status: "Pending",
    });

    const [form, setForm] = useState({
        dprNumber: "",
        workOrder: "",

        date: "",
        activitiesCompleted: [],
        manpower: {
            supervisors: 0,
            engineers: 0,
            skilled: 0,
            unskilled: 0,
            total: 0,
        },
        issuesRaised: false,
        issueDetails: [],
        nextDayPlan: "",
        weather: "",
        weatherImpact: "No",
        status: "Draft",
    });

    const [activityItem, setActivityItem] = useState({
        boqItem: "",
        activityMasterId: "",
        activity: "",
        description: "",
        location: "",
        drawingRef: "",
        todayQty: 0,
        previousQtyToDate: 0,
        totalQtyToDate: 0,
        quantityCompleted: 0,
        wastageQty: 0,
        status: "Pending",
    });

    const [issueItem, setIssueItem] = useState({
        issue: "",
        description: "",
        severity: "Low",
        status: "Open",
    });

    /* ========================================
    LOAD DPR DATA ON EDIT
    ======================================== */
    useEffect(() => {
        loadDpr();
        loadWorkOrders();
        fetchActivityMasters();
        fetchUnits();
    }, [id]);

    const fetchDprUsage = async (workOrderId) => {
        try {
            const res = await api.get(`/dpr/usage/${workOrderId}`, {
                params: { excludeDprId: id },
            });
            if (res.data?.success) {
                setDprUsageMap(res.data.map || {});
            }
        } catch {
            setDprUsageMap({});
        }
    };

    const fetchActivityMasters = async () => {
        try {
            const res = await api.get("/activity-masters");
            if (res.data?.success) {
                setActivityMasters(res.data.data || []);
            }
        } catch (error) {
            swal("Error", "Failed to fetch activity master", "error");
        }
    };

    const fetchUnits = async () => {
        try {
            const res = await api.get("/boq-unit-types");
            if (res.data?.success) {
                setUnits(res.data.data || []);
            }
        } catch (error) {
            swal("Error", "Failed to fetch unit master", "error");
        }
    };

    const loadDpr = async () => {
        try {
            const res = await api.get(`/dpr/${id}`);
            if (!res.data.success) throw new Error("DPR load failed");

            const d = res.data.data;

            setForm({
                dprNumber: d.dprNumber,
                workOrder: d.workOrder?._id || "",

                date: d.date?.substring(0, 10),
                activitiesCompleted: d.activitiesCompleted || [],
                manpower: d.manpower || {
                    supervisors: 0,
                    engineers: 0,
                    skilled: 0,
                    unskilled: 0,
                    total: 0,
                },
                issuesRaised: d.issuesRaised,
                issueDetails: d.issueDetails || [],
                nextDayPlan: d.nextDayPlan || "",
                weather: d.weather || "",
                weatherImpact: d.weatherImpact || "No",
                status: d.status,
            });
            setPhotos(
                (d.photos || []).map((p) => ({
                    existing: true,
                    photoId: p._id,
                    preview: getFileUrl(p.fileUrl || p.url || p.filePath || ""),
                    description: p.description || "",
                }))
            );
            // If DPR has saved task, load and set it later
            if (d.taskId) {
                setSelectedTask({
                    ...d.taskId,
                    employeeName: d.employeeUser?.name || "Unassigned",
                });
            }

            if (d.workOrder?._id || d.workOrder) {
                fetchPreviousOpenIssues(d.workOrder?._id || d.workOrder);
            }



        } catch (err) {
            swal("Error", "Failed to load DPR", "error");
            navigate(
                workOrderFromUrl
                    ? `/dpr?workOrder=${workOrderFromUrl}`
                    : "/dpr"
            );
        } finally {
            setFetching(false);
        }
    };
    const MAX_PHOTOS = 10;
    const MAX_FILE_SIZE_MB = 15;

    const handlePhotoChange = (e) => {
        const files = Array.from(e.target.files);

        // Count limit check
        if (photos.length + files.length > MAX_PHOTOS) {
            swal("Warning", `Maximum ${MAX_PHOTOS} photos allowed`, "warning");
            e.target.value = "";
            return;
        }

        // Size check per file
        const oversized = files.filter(
            (f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024
        );
        if (oversized.length > 0) {
            swal(
                "Warning",
                `Each photo must be under ${MAX_FILE_SIZE_MB}MB. ${oversized.map((f) => f.name).join(", ")} exceed the limit.`,
                "warning"
            );
            e.target.value = "";
            return;
        }

        const formatted = files.map((file) => ({
            file,
            description: "",
            preview: URL.createObjectURL(file),
        }));

        setPhotos((prev) => [...prev, ...formatted]);
        e.target.value = "";
    };

    const removePhoto = (index) => {
        setPhotos((prev) => {
            const updated = [...prev];
            if (updated[index].file && updated[index].preview) {
                URL.revokeObjectURL(updated[index].preview);
            }
            updated.splice(index, 1);
            return updated;
        });
    };
    /* ========================================
    LOAD WORK ORDERS
    ======================================== */
    const loadWorkOrders = async () => {
        try {
            const res = await api.get("/work-orders?limit=1000");
            if (res.data?.success) {
                const list = res.data.data;
                setWorkOrders(list);

                // Fix: Ensure BOQ loads if form.workOrder was already set by loadDpr
                if (form.workOrder) {
                    const matched = list.find((w) => w._id === form.workOrder);
                    if (matched) {
                        loadBOQ(matched._id);
                        if (matched.project?._id) {
                            fetchTasksByProject(matched.project._id);
                        }
                    }
                }
            }
        } catch (err) {
            console.error(err);
        }
    };

    /* ========================================
    LOAD BOQ BY PROJECT
    ======================================== */
    const loadBOQ = async (workOrderId) => {
        try {
            const res = await api.get(`/work-orders/${workOrderId}`);
            if (res.data?.success) {
                setBoqItems(res.data.data.boqItems || []);
            }
        } catch {
            swal("Error", "Failed to load BOQ", "error");
            setBoqItems([]);
        }
    };


    /* ========================================
    LOAD TASKS BY PROJECT
    ======================================== */
    const fetchTasksByProject = async (projectId, selectedTaskId = null) => {
        try {
            const res = await api.get(`/tasks/by-project/${projectId}`);
            if (res.data.success) {
                setTasks(res.data.data);

                // auto select saved task on edit
                if (selectedTaskId) {
                    const t = res.data.data.find((task) => task._id === selectedTaskId);
                    if (t) setSelectedTask(t);
                }
            }
        } catch (err) {
            swal("Error", "Failed to fetch tasks", "error");
        }
    };

    const fetchPreviousOpenIssues = async (workOrderId) => {
        if (!workOrderId) {
            setPreviousOpenIssues([]);
            setPreviousIssueUpdates({});
            return;
        }
        try {
            const res = await api.get(`/dpr/open-issues/${workOrderId}`, {
                params: { excludeDprId: id },
            });
            if (res.data?.success) {
                const issues = res.data.data || [];
                setPreviousOpenIssues(issues);
                const initialUpdates = {};
                issues.forEach((iss) => {
                    const key = iss.groupKey || iss.issueId || `${iss.dprId}_${iss.issueIndex}`;
                    initialUpdates[key] = {
                        groupKey: iss.groupKey,
                        items: iss.items || [
                            {
                                dprId: iss.dprId,
                                issueId: iss.issueId,
                                issueIndex: iss.issueIndex,
                            },
                        ],
                        originalStatus: iss.status || "Open",
                        status: iss.status || "Open",
                        resolutionRemarks: iss.resolutionRemarks || "",
                    };
                });
                setPreviousIssueUpdates(initialUpdates);
            }
        } catch {
            setPreviousOpenIssues([]);
            setPreviousIssueUpdates({});
        }
    };

    useEffect(() => {
        if (!form.workOrder || !workOrders.length) return;

        const wo = workOrders.find(w => w._id === form.workOrder);
        if (!wo) return;

        // [OK] BOQ comes from WorkOrder
        loadBOQ(wo._id);
        fetchDprUsage(wo._id);
        fetchPreviousOpenIssues(wo._id);

        // [OK] Tasks come from Project
        if (wo.project?._id) {
            fetchTasksByProject(wo.project._id);
        }

        setSelectedTask(null);
    }, [form.workOrder, workOrders]);



    /* ========================================
    FORM HANDLERS
    ======================================== */
    const handleChange = (e) => {
        setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    };

    const handleManpowerChange = (e) => {
        const { name, value } = e.target;
        const val = Number(value || 0);

        setForm((prev) => {
            const m = { ...prev.manpower, [name]: val };
            m.total = m.supervisors + m.engineers + m.skilled + m.unskilled;
            return { ...prev, manpower: m };
        });
    };

    const handleActivityChange = (e) => {
        const { name, value } = e.target;
        setActivityItem((prev) => {
            const next = { ...prev, [name]: value };
            if (name === "quantityCompleted" || name === "previousQtyToDate") {
                const doneQty = Number(next.quantityCompleted || 0);
                const prevQty = Number(next.previousQtyToDate || 0);
                next.todayQty = doneQty;
                next.totalQtyToDate = Number((prevQty + doneQty).toFixed(3));
            }
            return next;
        });
    };

    const addActivityItem = () => {
        if (!activityItem.boqItem) return swal("Warning", "Select BOQ", "warning");
        if (!activityItem.activityMasterId) return swal("Warning", "Select activity", "warning");
        if (!activityItem.activity.trim()) return swal("Warning", "Enter activity", "warning");
        if (activityItem.quantityCompleted <= 0) return swal("Warning", "Invalid qty", "warning");

        const selected = boqItems.find((b) => b._id === activityItem.boqItem);
        const prevQty = Number(activityItem.previousQtyToDate || 0);
        const doneQty = Number(activityItem.quantityCompleted || 0);
        const selectedActivity = activityMasters.find(
            (item) => item._id === activityItem.activityMasterId,
        );

        const newItem = {
            ...activityItem,
            activityMasterId: selectedActivity?._id || activityItem.activityMasterId,
            activity: selectedActivity?.name || activityItem.activity,
            boqName: `[WLI-${selected?.wlino || "-"}] ${selected?.itemName}`,
            unit: selected?.unit,
            boqQty: selected?.quantity,
            quantity: selected?.quantity,
            todayQty: doneQty,
            totalQtyToDate: Number(
                (prevQty + doneQty).toFixed(3),
            ),
        };

        setForm((prev) => ({
            ...prev,
            activitiesCompleted: [...prev.activitiesCompleted, newItem],
        }));

        setActivityItem({
            boqItem: "",
            activityMasterId: "",
            activity: "",
            description: "",
            location: "",
            drawingRef: "",
            todayQty: 0,
            previousQtyToDate: 0,
            totalQtyToDate: 0,
            quantityCompleted: 0,
            wastageQty: 0,
            status: "Pending",
        });

        swal("Success", "Activity added", "success");
    };
    const addManualActivity = () => {
        if (!manualItem.activity?.trim())
            return swal("Warning", "Enter manual activity", "warning");
        if (!manualItem.unit?.trim())
            return swal("Warning", "Select unit", "warning");

        setForm((prev) => ({
            ...prev,
            activitiesCompleted: [
                ...prev.activitiesCompleted,
                {
                    boqName: "Manual Activity",
                    boqQty: "-",
                    activity: manualItem.activity,
                    location: manualItem.location,
                    quantityCompleted: manualItem.quantity,
                    wastageQty: manualItem.wastage,
                    unit: manualItem.unit,
                    status: manualItem.status,
                },
            ],
        }));

        setManualItem({
            activity: "",
            quantity: "",
            unit: "",
            location: "",
            wastage: 0,
            status: "Pending",
        });
    };
    const removeActivityItem = (index) => {
        setForm((prev) => ({
            ...prev,
            activitiesCompleted: prev.activitiesCompleted.filter((_, i) => i !== index),
        }));
    };

    const addIssueItem = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (!issueItem.issue.trim())
            return swal("Warning", "Enter issue", "warning");

        const newIssue = { ...issueItem };
        setForm((prev) => ({
            ...prev,
            issueDetails: [...prev.issueDetails, newIssue],
            issuesRaised: true,
        }));

        setIssueItem({ issue: "", description: "", severity: "Low", status: "Open" });
    };

    const removeIssueItem = (e, index) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const updated = form.issueDetails.filter((_, i) => i !== index);

        setForm((prev) => ({
            ...prev,
            issueDetails: updated,
            issuesRaised: updated.length > 0,
        }));
    };

    /* ========================================
    SUBMIT UPDATED DPR
    ======================================== */
    const handleSubmit = async (e) => {
        e.preventDefault();

        const formData = new FormData();

        formData.append("workOrder", form.workOrder);
        formData.append("date", form.date);
        formData.append("nextDayPlan", form.nextDayPlan);
        formData.append("weather", form.weather);
        formData.append("weatherImpact", form.weatherImpact || "No");
        formData.append("status", form.status);

        formData.append("manpower", JSON.stringify(form.manpower));
        formData.append(
            "activitiesCompleted",
            JSON.stringify(form.activitiesCompleted)
        );

        const issuesToSubmit = [...form.issueDetails];
        if (issueItem.issue && issueItem.issue.trim()) {
            issuesToSubmit.push({ ...issueItem });
        }
        formData.append("issueDetails", JSON.stringify(issuesToSubmit));
        formData.append("issuesRaised", String(issuesToSubmit.length > 0));

        const updatesToSubmit = Object.values(previousIssueUpdates).filter(
            (u) => u.status !== u.originalStatus || (u.resolutionRemarks && u.resolutionRemarks.trim())
        );
        if (updatesToSubmit.length > 0) {
            formData.append("previousIssueUpdates", JSON.stringify(updatesToSubmit));
        }

        if (selectedTask?._id) formData.append("taskId", selectedTask._id);

        // HANDLE PHOTOS
        // We send:
        // 1. New photo files
        // 2. New photo descriptions
        // 3. Existing photo IDs (to keep)
        // 4. Existing photo descriptions

        const existingPhotos = photos.filter((p) => p.existing);
        const newPhotos = photos.filter((p) => !p.existing);

        // Send IDs of existing photos we want to KEEP
        formData.append("existingPhotoIds", JSON.stringify(existingPhotos.map(p => p.photoId)));

        // Send descriptions for existing photos (mapped by index/id)
        formData.append("existingPhotoDescriptions", JSON.stringify(existingPhotos.map(p => p.description)));

        newPhotos.forEach((p) => {
            formData.append("photos", p.file);
            formData.append("photoDescriptions", p.description);
        });

        setLoading(true);

        try {
            const res = await api.patch(`/dpr/${id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (res.data.success) {
                swal("Success", "DPR updated successfully!", "success");

                const projectFromUrl = searchParams.get("project");
                const filterQuery = [
                    projectFromUrl ? `project=${projectFromUrl}` : "",
                    workOrderFromUrl ? `workOrder=${workOrderFromUrl}` : ""
                ].filter(Boolean).join("&");

                navigate(filterQuery ? `/dpr?${filterQuery}` : "/dpr");
            }
        } catch (err) {
            swal("Error", err.response?.data?.message || "Update failed", "error");
        } finally {
            setLoading(false);
        }
    };
    const selectedBOQ = boqItems.find(
        (b) => b._id === activityItem.boqItem
    );
    const allTableIssues = [
        ...previousOpenIssues.map((p) => {
            const key = p.groupKey || p.issueId || `${p.dprId}_${p.issueIndex}`;
            return {
                severity: p.severity,
                status: previousIssueUpdates[key]?.status || p.status || "Open",
            };
        }),
        ...form.issueDetails,
    ];
    const issueStats = allTableIssues.reduce(
        (acc, issue) => {
            const severity = String(issue?.severity || "").toLowerCase();
            const status = String(issue?.status || "").toLowerCase();
            acc.total += 1;
            if (status === "open") acc.open += 1;
            if (status === "in progress") acc.inProgress += 1;
            if (status === "resolved") acc.resolved += 1;
            if (severity === "critical") acc.critical += 1;
            return acc;
        },
        { total: 0, open: 0, inProgress: 0, resolved: 0, critical: 0 },
    );
    const latestIssue = form.issueDetails[form.issueDetails.length - 1] || previousOpenIssues[0];
    /* ========================================
    LOADER
    ======================================== */
    if (fetching)
        return (
            <Fragment>
                <PageTitle activeMenu="Edit DPR" motherMenu="ITC" />
                <div className="text-center py-5">
                    <Spinner />
                    <p>Loading DPR...</p>
                </div>
            </Fragment>
        );

    /* ========================================
    UI
    ======================================== */
    return (
        <Fragment>
            <PageTitle activeMenu="Edit DPR" motherMenu="ITC" />

            <Row>
                <Col lg={12}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between">
                            <Card.Title>Edit DPR</Card.Title>
                            <Button
                                onClick={() =>
                                    navigate(
                                        workOrderFromUrl
                                            ? `/dpr?workOrder=${workOrderFromUrl}`
                                            : "/dpr"
                                    )
                                }
                            >
                                Back
                            </Button> </Card.Header>

                        <Card.Body>
                            <form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>DPR Number</Form.Label>
                                            <Form.Control value={form.dprNumber} readOnly />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Work Order *</Form.Label>
                                            <Form.Select
                                                className="form-control"
                                                name="workOrder"
                                                value={form.workOrder}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Work Order</option>
                                                {workOrders.map((wo) => (
                                                    <option key={wo._id} value={wo._id}>
                                                        {wo.woNo}{wo.label ? ` – ${wo.label}` : ""} - {wo.project?.projectName}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>

                                    </Col>

                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Date *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="date"
                                                value={form.date}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* TASK FIELD */}
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Task (Project Based)</Form.Label>
                                            <Form.Select
                                                className="form-control"
                                                value={selectedTask?._id || ""}
                                                onChange={(e) => {
                                                    const t = tasks.find((task) => task._id === e.target.value);
                                                    setSelectedTask(t || null);
                                                }}
                                            >
                                                <option value="">Select Task</option>
                                                {tasks.map((t) => (
                                                    <option key={t._id} value={t._id}>
                                                        {t.title} ({t.status})
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {selectedTask && (
                                        <Card className="p-2 mb-3 dpr-task-preview-card">
                                            <small><strong>Assigned To:</strong> {selectedTask.employeeName || "Unassigned"}</small><br />
                                            <small><strong>Task Description:</strong> {selectedTask.description || "N/A"}</small><br />
                                            <small><strong>Deadline:</strong> {selectedTask.plannedEndDate ? new Date(selectedTask.plannedEndDate).toLocaleDateString() : "N/A"}</small><br />
                                            <small><strong>Estimated Hours:</strong> {selectedTask.estimatedHours}</small><br />
                                            <small><strong>Status:</strong> {selectedTask.status}</small>
                                        </Card>
                                    )}
                                </Row>

                                {/* MANPOWER */}
                                <Row>
                                    {["supervisors", "engineers", "skilled", "unskilled"].map((role) => (
                                        <Col md={3} key={role}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{role.toUpperCase()}</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    name={role}
                                                    value={form.manpower[role]}
                                                    onChange={handleManpowerChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                    ))}
                                </Row>

                                <Row>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Total Manpower</Form.Label>
                                            <Form.Control value={form.manpower.total} readOnly />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* ACTIVITIES */}
                                {/* ACTIVITIES */}
                                <div className="mb-4">

                                    <h5 className="fw-bold mb-3">Activities Completed</h5>

                                    <Row className="g-3 align-items-end mb-3">

                                        {/* BOQ ITEM */}
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label>BOQ Item</Form.Label>

                                                <Select
                                                    styles={customSelectStyles}
                                                    classNamePrefix="boq-select"
                                                    placeholder="Search BOQ..."
                                                    options={boqItems.map((b) => ({
                                                        value: b._id,
                                                        label: `[WLI-${b.wlino || "-"}] ${b.itemName} (${b.unit})`,
                                                        data: b
                                                    }))}

                                                    value={
                                                        activityItem.boqItem
                                                            ? {
                                                                value: activityItem.boqItem,
                                                                label: selectedBOQ
                                                                    ? `[WLI-${selectedBOQ.wlino || "-"}] ${selectedBOQ.itemName} (${selectedBOQ.unit})`
                                                                    : ""
                                                            }
                                                            : null
                                                    }

                                                    onChange={(opt) =>
                                                        setActivityItem(prev => {
                                                            const next = {
                                                                ...prev,
                                                                boqItem: opt?.value || "",
                                                                unit: boqItems.find((b) => b._id === opt?.value)?.unit || "",
                                                                previousQtyToDate: dprUsageMap[opt?.value] || 0,
                                                            };
                                                            const doneQty = Number(next.quantityCompleted || 0);
                                                            next.todayQty = doneQty;
                                                            next.totalQtyToDate = Number((Number(next.previousQtyToDate || 0) + doneQty).toFixed(3));
                                                            return next;
                                                        })
                                                    }
                                                />

                                            </Form.Group>
                                        </Col>


                                        {/* BOQ QUANTITY */}
                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>BOQ Quantity</Form.Label>

                                                <Form.Control
                                                    readOnly
                                                    value={
                                                        selectedBOQ
                                                            ? `${selectedBOQ.quantity} ${selectedBOQ.unit}`
                                                            : ""
                                                    }
                                                />

                                            </Form.Group>
                                        </Col>


                                        <Col md={1}>
                                            <Form.Group>
                                                <Form.Label>Unit</Form.Label>

                                                <Form.Control
                                                    readOnly
                                                    value={selectedBOQ?.unit || ""}
                                                />

                                            </Form.Group>
                                        </Col>


                                        {/* ACTIVITY */}
                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Activity</Form.Label>

                                                <Form.Select
                                                    name="activityMasterId"
                                                    value={activityItem.activityMasterId}
                                                    onChange={(e) => {
                                                        const selected = activityMasters.find(
                                                            (item) => item._id === e.target.value,
                                                        );
                                                        setActivityItem((prev) => ({
                                                            ...prev,
                                                            activityMasterId: e.target.value,
                                                            activity: selected?.name || "",
                                                        }));
                                                    }}
                                                >
                                                    <option value="">Select Activity</option>
                                                    {activityMasters.map((activity) => (
                                                        <option key={activity._id} value={activity._id}>
                                                            {activity.name}
                                                        </option>
                                                    ))}
                                                </Form.Select>

                                            </Form.Group>
                                        </Col>


                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Location / Chainage</Form.Label>

                                                <Form.Control
                                                    type="text"
                                                    name="location"
                                                    value={activityItem.location}
                                                    onChange={handleActivityChange}
                                                    maxLength={80}
                                                    placeholder="Block A - Floor 3"
                                                />

                                            </Form.Group>
                                        </Col>


                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Drawing Ref.</Form.Label>

                                                <Form.Control
                                                    type="text"
                                                    name="drawingRef"
                                                    value={activityItem.drawingRef}
                                                    onChange={handleActivityChange}
                                                    placeholder="DWG-01"
                                                />

                                            </Form.Group>
                                        </Col>


                                        <Col md={1}>
                                            <Form.Group>
                                                <Form.Label>Today Qty</Form.Label>

                                                <Form.Control
                                                    type="number"
                                                    value={activityItem.todayQty}
                                                    readOnly
                                                />

                                            </Form.Group>
                                        </Col>


                                        <Col md={1}>
                                            <Form.Group>
                                                <Form.Label>Prev. Qty</Form.Label>

                                                <Form.Control
                                                    type="number"
                                                    name="previousQtyToDate"
                                                    value={activityItem.previousQtyToDate}
                                                    onChange={handleActivityChange}
                                                    readOnly
                                                />

                                            </Form.Group>
                                        </Col>


                                        <Col md={1}>
                                            <Form.Group>
                                                <Form.Label>Total Qty</Form.Label>

                                                <Form.Control
                                                    type="number"
                                                    value={activityItem.totalQtyToDate}
                                                    readOnly
                                                />

                                            </Form.Group>
                                        </Col>


                                        {/* COMPLETED */}
                                        <Col md={1}>
                                            <Form.Group>
                                                <Form.Label>Done</Form.Label>

                                                <Form.Control
                                                    type="number"
                                                    name="quantityCompleted"
                                                    value={activityItem.quantityCompleted}
                                                    onChange={handleActivityChange}
                                                    min={0}
                                                    
                                                />

                                            </Form.Group>
                                        </Col>


                                        {/* WASTAGE */}
                                        <Col md={1}>
                                            <Form.Group>
                                                <Form.Label>Waste</Form.Label>

                                                <Form.Control
                                                    type="number"
                                                    name="wastageQty"
                                                    value={activityItem.wastageQty}
                                                    onChange={handleActivityChange}
                                                />

                                            </Form.Group>
                                        </Col>


                                        {/* STATUS */}
                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Status</Form.Label>

                                                <Form.Select
                                                    className="form-control"
                                                    name="status"
                                                    value={activityItem.status}
                                                    onChange={handleActivityChange}
                                                >
                                                    <option>Pending</option>
                                                    <option>In Progress</option>
                                                    <option>Completed</option>
                                                    <option>QC Hold</option>
                                                </Form.Select>

                                            </Form.Group>
                                        </Col>


                                        {/* ADD BUTTON */}
                                        <Col md={1}>
                                            <Button
                                                style={{
                                                    height: `${compactControlHeight}px`,
                                                    minHeight: `${compactControlHeight}px`,
                                                    paddingTop: 0,
                                                    paddingBottom: 0,
                                                }}
                                                className="w-100"
                                                onClick={addActivityItem}
                                            >
                                                Add
                                            </Button>
                                        </Col>

                                    </Row>


                                    {/* WASTAGE PERCENTAGE */}
                                    {selectedBOQ && activityItem.wastageQty > 0 && (
                                        <small className="text-danger">
                                            Wastage:{" "}
                                            {(
                                                (activityItem.wastageQty /
                                                    selectedBOQ.quantity) *
                                                100
                                            ).toFixed(2)}
                                            %
                                        </small>
                                    )}
                                    <Row className="g-3 align-items-end mb-3">

                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Manual Activity</Form.Label>

                                                <Form.Control
                                                    value={manualItem.activity}
                                                    placeholder="Activity"
                                                    onChange={(e) =>
                                                        setManualItem(prev => ({
                                                            ...prev,
                                                            activity: e.target.value
                                                        }))
                                                    }
                                                />

                                            </Form.Group>
                                        </Col>


                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Quantity</Form.Label>

                                                <Form.Control
                                                    type="number"
                                                    value={manualItem.quantity}
                                                    onChange={(e) =>
                                                        setManualItem(prev => ({
                                                            ...prev,
                                                            quantity: e.target.value
                                                        }))
                                                    }
                                                />

                                            </Form.Group>
                                        </Col>


                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Unit</Form.Label>

                                                <Form.Select
                                                    value={manualItem.unit}
                                                    onChange={(e) =>
                                                        setManualItem(prev => ({
                                                            ...prev,
                                                            unit: e.target.value
                                                        }))
                                                    }
                                                >
                                                    <option value="">Select Unit</option>
                                                    {units.map((unit) => (
                                                        <option key={unit._id} value={unit.symbol || unit.name}>
                                                            {unit.name}
                                                            {unit.symbol ? ` (${unit.symbol})` : ""}
                                                        </option>
                                                    ))}
                                                </Form.Select>

                                            </Form.Group>
                                        </Col>


                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Location</Form.Label>

                                                <Form.Control
                                                    value={manualItem.location}
                                                    placeholder="Block A - Floor 3"
                                                    onChange={(e) =>
                                                        setManualItem(prev => ({
                                                            ...prev,
                                                            location: e.target.value
                                                        }))
                                                    }
                                                />

                                            </Form.Group>
                                        </Col>


                                        <Col md={1}>
                                            <Form.Group>
                                                <Form.Label>Waste</Form.Label>

                                                <Form.Control
                                                    type="number"
                                                    value={manualItem.wastage}
                                                    onChange={(e) =>
                                                        setManualItem(prev => ({
                                                            ...prev,
                                                            wastage: e.target.value
                                                        }))
                                                    }
                                                />

                                            </Form.Group>
                                        </Col>


                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Status</Form.Label>

                                                <Form.Select
                                                    className="form-control"
                                                    value={manualItem.status}
                                                    onChange={(e) =>
                                                        setManualItem(prev => ({
                                                            ...prev,
                                                            status: e.target.value
                                                        }))
                                                    }
                                                >
                                                    <option>Pending</option>
                                                    <option>In Progress</option>
                                                    <option>Completed</option>
                                                </Form.Select>

                                            </Form.Group>
                                        </Col>


                                        <Col md={2}>
                                            <Button
                                                variant="warning"
                                                className="w-100"
                                                type="button"
                                                onClick={addManualActivity}
                                            >
                                                Add Manual
                                            </Button>
                                        </Col>

                                    </Row>

                                    {/* TABLE */}
                                    {form.activitiesCompleted.length > 0 && (

                                        <div className="table-responsive">
                                            <Table striped bordered hover size="sm" style={{ minWidth: "1200px" }}>

                                                <thead>
                                                    <tr>
                                                        <th>BOQ Item</th>
                                                        <th>Activity</th>
                                                        <th>BOQ Qty</th>
                                                        <th>Unit</th>
                                                        <th>Location / Chainage</th>
                                                        <th>Drawing Ref.</th>
                                                        <th>Today Qty</th>
                                                        <th>Prev. Qty</th>
                                                        <th>Total Qty</th>
                                                        <th>Completed</th>
                                                        <th>Wastage</th>
                                                        <th>Status</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>

                                                <tbody>

                                                    {form.activitiesCompleted.map((item, i) => (
                                                        <tr key={i}>

                                                            <td>{item.boqName}</td>

                                                            <td>{item.activity}</td>

                                                            <td>{item.boqQty ?? item.quantity ?? "-"}</td>

                                                            <td>{item.unit || "-"}</td>

                                                            <td>{item.location || "-"}</td>

                                                            <td>{item.drawingRef || "-"}</td>

                                                            <td>{item.todayQty ?? item.quantityCompleted}</td>

                                                            <td>{item.previousQtyToDate ?? 0}</td>

                                                            <td>{item.totalQtyToDate ?? item.quantityCompleted}</td>

                                                            <td>{item.quantityCompleted} {item.unit}</td>

                                                            <td className="text-danger fw-bold">
                                                                {item.wastageQty || 0} {item.unit}
                                                            </td>

                                                            <td>{item.status}</td>

                                                            <td>
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => removeActivityItem(i)}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </td>

                                                        </tr>
                                                    ))}

                                                </tbody>
                                            </Table>
                                        </div>

                                    )}

                                </div>


                                {/* ISSUES */}
                                <div className="mb-4">
                                    <Form.Label className="fw-bold">Issues</Form.Label>

                                    <Card className="mb-3 border-0 shadow-sm dpr-issue-snapshot-card">
                                        <Card.Body className="py-3">
                                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                                <div>
                                                    <div className="fw-semibold">Issue Snapshot</div>
                                                    <small className="text-muted">
                                                        Track unresolved items clearly. Critical issues should be visible before submission.
                                                    </small>
                                                </div>
                                                <span className="badge bg-secondary text-white">{issueStats.total} total</span>
                                            </div>

                                            <div className="d-flex flex-wrap gap-2">
                                                <span className="badge bg-danger">Critical {issueStats.critical}</span>
                                                <span className="badge bg-warning text-dark">Open {issueStats.open}</span>
                                                <span className="badge bg-info text-dark">In Progress {issueStats.inProgress}</span>
                                                <span className="badge bg-success">Resolved {issueStats.resolved}</span>
                                            </div>

                                            <div className="mt-2 small text-muted">
                                                {latestIssue ? (
                                                    <>
                                                        Latest issue: <strong>{latestIssue.issue}</strong> (
                                                        {latestIssue.severity} / {latestIssue.status})
                                                    </>
                                                ) : (
                                                    "No issues added yet."
                                                )}
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="g-2 mb-3 align-items-end">
                                        <Col md={3}>
                                            <Form.Label className="small text-muted mb-1">Issue</Form.Label>
                                            <Form.Control
                                                name="issue"
                                                placeholder="Enter issue..."
                                                value={issueItem.issue}
                                                onChange={(e) => setIssueItem((p) => ({ ...p, issue: e.target.value }))}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        addIssueItem(e);
                                                    }
                                                }}
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Form.Label className="small text-muted mb-1">Description</Form.Label>
                                            <Form.Control
                                                name="description"
                                                placeholder="Description..."
                                                value={issueItem.description}
                                                onChange={(e) => setIssueItem((p) => ({ ...p, description: e.target.value }))}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        addIssueItem(e);
                                                    }
                                                }}
                                            />
                                        </Col>

                                        <Col md={2}>
                                            <Form.Label className="small text-muted mb-1">Severity</Form.Label>
                                            <Form.Select
                                                className="form-control"
                                                name="severity"
                                                value={issueItem.severity}
                                                onChange={(e) => setIssueItem((p) => ({ ...p, severity: e.target.value }))}
                                            >
                                                <option>Low</option>
                                                <option>Medium</option>
                                                <option>High</option>
                                                <option>Critical</option>
                                            </Form.Select>
                                        </Col>

                                        <Col md={2}>
                                            <Form.Label className="small text-muted mb-1">Status</Form.Label>
                                            <Form.Select
                                                className="form-control"
                                                name="status"
                                                value={issueItem.status}
                                                onChange={(e) => setIssueItem((p) => ({ ...p, status: e.target.value }))}
                                            >
                                                <option>Open</option>
                                                <option>In Progress</option>
                                                <option>Resolved</option>
                                            </Form.Select>
                                        </Col>

                                        <Col md={1}>
                                            <Button type="button" variant="primary" className="w-100" onClick={addIssueItem}>
                                                + Add
                                            </Button>
                                        </Col>
                                    </Row>

                                    {(form.issueDetails.length > 0 || previousOpenIssues.length > 0) && (
                                                            <div className="table-responsive">
                                                                <Table bordered hover size="sm" className="dpr-issues-table align-middle">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Issue</th>
                                                                            <th>Description</th>
                                                                            <th style={{ width: "110px" }}>Severity</th>
                                                                            <th style={{ width: "140px" }}>Status</th>
                                                                            <th className="text-center" style={{ width: "130px" }}>Action / Source</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {/* Previous DPR Open Issues */}
                                                                        {previousOpenIssues.map((item, i) => {
                                                                            const key = item.groupKey || item.issueId || `${item.dprId}_${item.issueIndex}`;
                                                                            const currentStatus = previousIssueUpdates[key]?.status || item.status || "Open";
                                                                            return (
                                                                                <tr key={`prev-${key || i}`} className="dpr-prev-issue-row">
                                                                                    <td>
                                                                                        <div className="d-flex align-items-center gap-1 mb-1">
                                                                                            <span className="badge bg-warning text-dark me-1" style={{ fontSize: "10px", fontWeight: "600" }}>
                                                                                                Prev DPR
                                                                                            </span>
                                                                                            <strong className="dpr-issue-title">{item.issue}</strong>
                                                                                            {item.occurrences > 1 && (
                                                                                                <span className="badge bg-danger ms-1" style={{ fontSize: "10px" }}>
                                                                                                    {item.occurrences}x
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                        <small className="text-muted d-block" style={{ fontSize: "11px" }}>
                                                                                            {item.dprNumbers?.join(", ") || item.latestDprNumber || item.dprNumber || "Previous DPR"}
                                                                                        </small>
                                                                                    </td>
                                                                                    <td>
                                                                                        <div className="dpr-issue-desc">{item.description || item.descriptions?.[0] || "-"}</div>
                                                                                        {item.variants?.length > 1 && (
                                                                                            <small className="text-muted d-block fst-italic mt-1" style={{ fontSize: "10px" }}>
                                                                                                {item.variants.join(" | ")}
                                                                                            </small>
                                                                                        )}
                                                                                    </td>
                                                                                    <td>
                                                                                        <span className={`badge ${item.severity === "Critical" || item.severity === "High" ? "bg-danger text-white" : item.severity === "Medium" ? "bg-warning text-dark" : "bg-secondary text-white"}`}>
                                                                                            {item.severity}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td style={{ minWidth: "120px" }}>
                                                                                        <Form.Select
                                                                                            size="sm"
                                                                                            className="form-control form-control-sm"
                                                                                            value={currentStatus}
                                                                                            onChange={(e) => {
                                                                                                const newStatus = e.target.value;
                                                                                                setPreviousIssueUpdates((prev) => ({
                                                                                                    ...prev,
                                                                                                    [key]: {
                                                                                                        ...(prev[key] || {
                                                                                                            groupKey: item.groupKey,
                                                                                                            items: item.items || [{ dprId: item.dprId, issueId: item.issueId, issueIndex: item.issueIndex }],
                                                                                                            originalStatus: item.status || "Open",
                                                                                                            resolutionRemarks: "",
                                                                                                        }),
                                                                                                        status: newStatus,
                                                                                                    },
                                                                                                }));
                                                                                            }}
                                                                                        >
                                                                                            <option value="Open">Open</option>
                                                                                            <option value="In Progress">In Progress</option>
                                                                                            <option value="Resolved">Resolved</option>
                                                                                        </Form.Select>
                                                                                    </td>
                                                                                    <td className="text-center">
                                                                                        <span className="badge dpr-prev-badge">
                                                                                            Prev DPR Issue
                                                                                        </span>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}

                                                                        {/* Current DPR Issues */}
                                                                        {form.issueDetails.map((item, i) => (
                                                                            <tr key={`curr-${i}`} className="dpr-curr-issue-row">
                                                                                <td>
                                                                                    <div className="d-flex align-items-center gap-1">
                                                                                            <span className="badge bg-primary text-white me-1" style={{ fontSize: "10px", fontWeight: "600" }}>
                                                                                                Current DPR
                                                                                            </span>
                                                                                            <strong className="dpr-issue-title">{item.issue}</strong>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="dpr-issue-desc">{item.description || "-"}</div>
                                                                                </td>
                                                                                <td>
                                                                                        <span className={`badge ${item.severity === "Critical" || item.severity === "High" ? "bg-danger text-white" : item.severity === "Medium" ? "bg-warning text-dark" : "bg-secondary text-white"}`}>
                                                                                            {item.severity}
                                                                                        </span>
                                                                                </td>
                                                                                    <td>
                                                                                        <span className={`badge ${item.status === "Resolved" ? "bg-success text-white" : item.status === "In Progress" ? "bg-info text-dark" : "bg-warning text-dark"}`}>
                                                                                            {item.status}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="text-center">
                                                                                        <Button type="button" variant="danger" size="sm" onClick={(e) => removeIssueItem(e, i)}>
                                                                                            Remove
                                                                                        </Button>
                                                                                    </td>
                                                                            </tr>
                                                    ))}
                                                            </tbody>
                                                        </Table>
                                        </div>
                                    )}
                                        </div>
                                {/* SITE PHOTOS */}
                                    <div className="mb-4">
                                        <Form.Label className="fw-bold">Site Photos</Form.Label>

                                        <Form.Control
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={handlePhotoChange}
                                            className="mb-3"
                                        />

                                        {photos.length > 0 && (
                                            <Row>
                                                {photos.map((photo, i) => (
                                                    <Col md={3} key={i} className="mb-3">
                                                        <div className="position-relative">
                                                            <img
                                                                src={photo.preview}
                                                                alt="preview"
                                                                style={{
                                                                    width: "100%",
                                                                    height: "120px",
                                                                    objectFit: "cover",
                                                                    borderRadius: "6px",
                                                                    border: "1px solid #ddd",
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger btn-xs position-absolute top-0 end-0 m-1"
                                                                style={{ padding: "2px 5px" }}
                                                                onClick={() => removePhoto(i)}
                                                            >
                                                                <i className="fas fa-times"></i>
                                                            </button>
                                                        </div>

                                                        <Form.Control
                                                            className="mt-2"
                                                            placeholder="Photo details..."
                                                            value={photo.description}
                                                            onChange={(e) => {
                                                                const updated = [...photos];
                                                                updated[i].description = e.target.value;
                                                                setPhotos(updated);
                                                            }}
                                                        />
                                                    </Col>
                                                ))}
                                            </Row>
                                        )}
                                    </div>
                                    {/* NEXT DAY PLAN */}
                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-semibold">Next Day Plan</Form.Label>
                                        <div className="dpr-editor-wrapper">
                                            <ReactQuill
                                                theme="snow"
                                                value={form.nextDayPlan || ""}
                                                onChange={(val) => setForm((prev) => ({ ...prev, nextDayPlan: val }))}
                                                modules={quillModules}
                                                placeholder="Enter next day planned activities, manpower/material targets..."
                                                style={{ minHeight: "120px" }}
                                            />
                                        </div>
                                    </Form.Group>

                                    {/* WEATHER & WEATHER IMPACT */}
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Weather</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="weather"
                                                    placeholder="e.g. Sunny, Rainy, Cloudy"
                                                    value={form.weather}
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Weather Impacted? (Work Affected)</Form.Label>
                                                <Form.Select
                                                    className="form-control"
                                                    name="weatherImpact"
                                                    value={form.weatherImpact || "No"}
                                                    onChange={handleChange}
                                                >
                                                    <option value="No">No</option>
                                                    <option value="Yes">Yes</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {/* STATUS */}
                                    <Form.Group className="mb-4">
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select
                                            className="form-control"
                                            name="status"
                                            value={form.status}
                                            onChange={handleChange}
                                        >
                                            <option value="Draft">Draft</option>
                                            <option value="Submitted">Submitted</option>
                                        </Form.Select>
                                    </Form.Group>

                                    {/* BUTTONS */}
                                    <div className="mt-4 d-flex gap-2">
                                        <Button variant="primary" type="submit" disabled={loading}>
                                            {loading ? <Spinner size="sm" /> : "Update DPR"}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            disabled={loading}
                                            onClick={() =>
                                                navigate(
                                                    workOrderFromUrl
                                                        ? `/dpr?workOrder=${workOrderFromUrl}`
                                                        : "/dpr"
                                                )
                                            }
                                        >
                                            Cancel
                                        </Button>
                                    </div>

                            </form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

export default DprEdit;
